import { TransferTypes } from '@constants/miscellaneous';
import { IAssetKhipuInfo } from './asset';

export enum MovementListParentType {
  Users = 'users',
  Portfolios = 'portfolios',
}

export const PARENT_TYPE_TO_FIELD_MAP: Record<MovementListParentType, string> = {
  [MovementListParentType.Users]: 'userId',
  [MovementListParentType.Portfolios]: 'portfolioId',
};

export enum OrderOperation {
  Withdrawal = 'withdrawal',
  Deposit = 'deposit',
  Contribution = 'contribution',
}

export interface IMovement {
  id: string;
  amount: number;
  date: Date;
  portfolioId: string;
  status: string;
  transferId: string;
  userId: string;
  type: string;
  typeClass: string;
  orderId: string;
  executionDate?: any;
  createdAt: any;
  updatedAt: any;
  operation: OrderOperation;
  proposedAmount: number;
  cashExcess?: number;
  proposedShareVariation: IShareVariation[];
  executedAmount?: number;
  executedShareVariation?: IShareVariation[];
  referral?: boolean;
  referred?: boolean;
  referralName?: string;
  isPassive?: boolean;
  finalWithdrawalAmount?: number;
  finalDepositAmount?: number;
  amountUSD?: number;
  isStockTransfer?: boolean;
  finalWithdrawalAmountUSD?: number;
  executedAmountUSD?: number;
  proposedAmountUSD?: number;
  onlyCashExcess?: boolean;
  transferType?: TransferTypes;
}

export interface IEnrichedListMovement extends IMovement {
  amountToShow?: number;
  displayUSD?: boolean;
  routerLink?: string[];
  queryParams?: Record<string, string>;
  year?: string;
}

export interface IDeposit {
  transferType?: TransferTypes;
  amountUSD?: number;
  isBuy?: boolean;
  amount: number;
  createdAt: any;
  updatedAt: any;
  status: string;
  id: string;
  userId: string;
  referral?: boolean;
  referred?: boolean;
  referralName?: string;
  referralCommissionDate?: string;
  referralCommissionDetail?: string[];
  contributionStrategy?: any;
  fromPortfolioTransfer?: boolean;
  sentToDW?: boolean;
  referrerStockAssetId?: string;
  executedShareVariations?: IShareVariation[];
  proposedShareVariations?: IShareVariation[];
  transferWithdrawalId?: string;
  fromRecurring?: boolean;
  redistributedToCoverNegativeCash?: boolean;
  isDWOrder?: boolean;
  isCashInBuyingPower?: boolean;
  transferWithdrawalPortfolioName?: string;
  hasFinalUSDPrice?: boolean;
}

export interface IDepositRedistribution {
  depositId: string;
  contributionStrategy: IContributionStrategy;
  id: string;
  status: string;
  userId: string;
  depositRef: firebase.default.firestore.DocumentReference;
  saveDistribution: boolean;
}

export interface IWithdrawal {
  isCashOutBuyingPower?: boolean;
  amount: number;
  createdAt: any;
  updatedAt: any;
  status: string;
  portfolioName: string;
  portfolioId: string;
  transferDate?: any;
  finalWithdrawalAmount?: number;
  id: string;
  userId: string;
  referralDiscount?: number;
  onlyCashExcess?: boolean;
  sent: boolean;
  sentAtLeastOnce?: boolean;
  transferPortfolioId?: string;
  transferPortfolioName?: string;
  transferDepositId?: string;
  paused?: boolean;
  finalWithdrawalAmountUSD?: number;
  amountUSD?: number;
  finalUSDPrice?: number;
  isSell?: boolean;
  uniqueAssetId?: string;
  isCustodyTransfer?: boolean;
  executedShareVariation?: IShareVariation[];
  isCoveringNegativeCash?: boolean;
  drivewealthTransferToLocalBankDate?: any;
  shouldSendToWithdrawalsApi?: boolean;
  transferError?: boolean;
  errorMessage?: string;
  hasFinalUSDPrice?: boolean;
  fxDate?: any;
  isDWOrder?: boolean;
  transferType?: TransferTypes;
  intent?: WithdrawalIntent;
  scope?: WithdrawalScope;
}

export interface ICorporateEventDetails {
  splitRatio?: string;
  fromShares?: number;
  toShares?: number;
  acquirer?: { symbol: string; name: string };
  acquiree?: { symbol: string; name: string };
  cashAmount?: number;
  receivedSymbol?: string;
  receivedShares?: number;
  exchangeRatio?: string;
  premiumPercent?: number;
  parentSymbol?: string;
  parentName?: string;
  parentShares?: number;
  spinoffSymbol?: string;
  spinoffName?: string;
  spinoffRatio?: string;
  spinoffShares?: number;
  specialDividendPerShare?: number;
}

export type CorporateEventActionType = 'SPLIT' | 'MERGER' | 'SPINOFF';

export interface IPassiveMovement {
  amount: number;
  executionDate: any;
  assetId?: string;
  portfolioId?: string;
  commissionDate?: string;
  id: string;
  type: string;
  operation: string;
  isPassive: boolean;
  createdAt: any;
  typeClass: string;
  isUSDDividend?: boolean;
  isDWDividend?: boolean;
  isInterest?: boolean;
  amountUSD?: number;
  DIV?: number;
  DIVTAX?: number;
  isRebateInterest?: boolean;
  amountTax?: number;
  amountBeforeTax?: number;
  portfolios?: Array<{
    portfolioId: string;
    cashAmount: number;
    investedAmount?: number;
    totalCashAmount?: number;
  }>;
  isCorporateEvent?: boolean;
  actionType?: CorporateEventActionType;
  positionDelta?: number;
  corporateEventDescription?: string;
  corporateEventDetails?: ICorporateEventDetails;
}

export interface IShareVariation {
  assetId: string;
  shareVariation: number;
  shareAveragePrice: number;
  amount: number;
  amountUSD?: number;
  shareAveragePriceUSD?: number;
}

export interface IContributionStrategy {
  portfolios: IContributionStrategyItem[];
  userId: string;
  uniqueAssetId?: string;
  assets?: IAssetKhipuInfo[];
  isDrivewealth?: boolean;
  fx?: number;
}

export interface IContributionStrategyItem {
  isDrivewealth?: boolean;
  portfolioName?: string;
  portfolioId: string;
  percentage: number;
  isCashInBuyingPower?: boolean;
}

export interface IMovementAssetItem {
  name: string;
  sharePrice: number;
  amount: number;
  assetId: string;
  shares: number;
  amountUSD?: number;
}

export interface IMovementPortfolioDistributionItem {
  portfolioName: string;
  percentage: number;
  portfolioId: string;
  status: string;
  distributionAmount?: number;
  executedAmountUSD?: number;
}

export enum OrderCancellationErrorTypes {
  InstantOrder = 'INSTANT_ORDER',
  OrderComplete = 'ORDER_COMPLETE',
  CancelInProgress = 'CANCEL_IN_PROGRESS',
  PartiallyFilled = 'PARTIALLY_FILLED',
}

enum OrderCancellationErrorMessages {
  InstantOrder = 'Tu orden es de ejecución instantánea, no se puede cancelar.',
  OrderComplete = 'Tu orden ya se ejecutó en el mercado, no se puede cancelar.',
  CancelInProgress = 'Tu orden se está cancelando. Te avisaremos cuando esté listo.',
  PartiallyFilled = 'Tu orden se ejecutó parcialmente, no se puede cancelar. Te avisaremos apenas se complete.',
}

export const orderCancellationErrorDict: Record<OrderCancellationErrorTypes, OrderCancellationErrorMessages> = {
  [OrderCancellationErrorTypes.InstantOrder]: OrderCancellationErrorMessages.InstantOrder,
  [OrderCancellationErrorTypes.OrderComplete]: OrderCancellationErrorMessages.OrderComplete,
  [OrderCancellationErrorTypes.CancelInProgress]: OrderCancellationErrorMessages.CancelInProgress,
  [OrderCancellationErrorTypes.PartiallyFilled]: OrderCancellationErrorMessages.PartiallyFilled,
};

export enum IDocumentDeleteCollections {
  Buys = 'buyDeletes',
  Withdrawals = 'withdrawalDeletes',
}

export enum IDocumentDeleteStatus {
  Created = 'created',
  Failed = 'failed',
  Complete = 'complete',
  CancelInProgress = 'cancelInProgress',
}

export interface IDocumentDelete {
  userId: string;
  status: IDocumentDeleteStatus;
  errorReason?: string;
  errorType?: OrderCancellationErrorTypes;
}

export interface IBuyDelete extends IDocumentDelete {
  depositId: string;
}

export interface IWithdrawalDelete extends IDocumentDelete {
  withdrawalId: string;
}

export enum WithdrawalStatus {
  Sent = 'sent',
  Created = 'created',
  Executed = 'executed',
  Complete = 'complete',
  Failed = 'failed',
  Incomplete = 'incomplete',
  SentToWithdrawalsApi = 'sentToWithdrawalsApi',
  FailedToSendToWithdrawalsApi = 'failedToSendToWithdrawalsApi',
  ToSendToWithdrawalsApi = 'toSendToWithdrawalsApi',
}

export enum WithdrawalIntent {
  PortfolioWithdrawal = 'portfolioWithdrawal',
  StockSell = 'stockSell',
  UsdWithdrawal = 'usdWithdrawal',
  PortfolioToPortfolioTransfer = 'portfolioToPortfolioTransfer',
  PortfolioToStocksTransfer = 'portfolioToStocksTransfer',
  StocksToPortfolioTransfer = 'stocksToPortfolioTransfer',
}

export enum WithdrawalScope {
  Total = 'total',
  Partial = 'partial',
}

export enum DepositStatus {
  Created = 'created',
  WaitingContributionStrategy = 'waiting_contribution_strategy',
  Sent = 'sent',
  Complete = 'complete',
  Failed = 'failed',
}

export enum OrderStatus {
  Created = 'created',
  Sent = 'sent',
  Complete = 'complete',
  Rejected = 'rejected',
  Canceled = 'canceled',
  Failed = 'failed',
}

export enum MovementStatus {
  Complete = 'complete',
  Failed = 'failed',
}

export enum IDocumentDeleteToasts {
  portfolioTransfer = 'Tus órdenes de compras y ventas fueron canceladas exitosamente.',
  success = 'Tu orden se canceló exitosamente.',
  cancelInProgress = 'Tu orden se está cancelando. Te avisaremos cuando esté listo.',
  portfolioTransferError = 'Error al eliminar el retiro, inténtalo nuevamente.',
  genericError = 'Algo ocurrió al intentar cancelar tu orden. Inténtalo de nuevo.',
  withdrawalError = 'Error al eliminar el retiro, inténtalo nuevamente.',
}

export enum PortfolioToPortfolioSteps {
  Created = 'created',
  Sent = 'sent',
  Complete = 'complete',
}
